import React, { Component } from 'react'
import { withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import compose from 'lodash/fp/compose'

import { getComicbookById } from 'client/bookmate/reducers/comicbooks-reducer'
import { getImpressionsByIds } from 'client/bookmate/reducers/impressions-reducer'
import { getAllEmotions } from 'client/bookmate/reducers/emotions-reducer'

import { getCurrentSeries } from 'client/bookmate/helpers/series-helpers'
import { getSecondaryAuthors } from 'client/bookmate/helpers/author-helpers'
import { prepareDefinitionListData } from 'client/shared/helpers/definition-list-helpers'
import errorBoundary from 'client/shared/decorators/error-boundary'
import urlFor, { QueryParams } from 'shared/tools/url-helper'

import withMarketing, {
  MarketingProps,
} from 'client/shared/decorators/with-marketing'

import Spacer from 'client/shared/blocks/spacer'
import Linka from 'client/shared/blocks/linka'
import List from 'client/bookmate/blocks/list'

import ImpressionsIndex from 'client/bookmate/blocks/impressions-index'
import ComicbookShelvesIndexBox from 'client/bookmate/boxes/comicbook-shelves-index-box'

import ExpandableText from 'client/shared/blocks/expandable-text'
import { PaperPages } from 'client/bookmate/blocks/paper-pages'
import { UnavailableInfo } from 'client/bookmate/blocks/unavailable-info'
import DefinitionList from 'client/shared/blocks/definition-list'
import Topic from 'client/bookmate/blocks/topic'
import TopicList from 'client/bookmate/blocks/topic-list'
import ExternalLink from 'client/bookmate/blocks/external-link'

import { ComicbookProps } from 'client/shared/types/comicbook'
import { ImpressionProps } from 'client/shared/types/impression'
import { EmotionRatingProps, EmotionProps } from 'client/shared/types/emotion'
import { State } from 'shared/types/redux'
import { getPublicationYear } from 'client/shared/helpers/date-helpers'
import RelatedOrSeriesEpisodes from '../blocks/related-or-series-episodes/related-or-series-episodes'

type Props = {
  comicbook: ComicbookProps
  impressionsIndex: ImpressionProps[]
  emotionRating: EmotionRatingProps[]
  emotions: EmotionProps[]
  hasCurrentUserImpression: boolean
  fromSeries?: string
  userDevice: string
  query?: QueryParams
  publisherResources: ComicbookProps[]
} & withI18nProps &
  MarketingProps

class ComicbookIndexBox extends Component<Props> {
  getSecondaryData() {
    const {
      i18n,
      comicbook: {
        series_list,
        illustrators = [],
        translators = [],
        publishers = [],
        owner_catalog_title,
        original_year,
        publication_date,
      },
      fromSeries,
      query,
    } = this.props

    const series = getCurrentSeries(series_list, fromSeries)

    return [
      {
        resource: owner_catalog_title,
        label: i18n._('book.owner'),
        value: [owner_catalog_title],
      },
      {
        resource: original_year,
        label: i18n._('book.original_year'),
        value: [original_year],
      },
      {
        resource: publication_date,
        label: i18n._('book.publication_year'),
        value: [getPublicationYear(publication_date)],
      },
      {
        resource: series,
        label: i18n._('book.series'),
        value: series ? (
          <Linka path={urlFor.series(series.uuid, query)}>{series.title}</Linka>
        ) : null,
      },
      {
        resource: publishers,
        label: i18n._('book.publisher', { count: publishers.length }),
        value: getSecondaryAuthors(publishers, query),
      },
      {
        resource: translators,
        label: i18n._('book.translator', { count: translators.length }),
        value: getSecondaryAuthors(translators, query),
      },
      {
        resource: illustrators,
        label: i18n._('book.artist', { count: illustrators.length }),
        value: getSecondaryAuthors(illustrators, query),
      },
    ]
  }

  renderSecondaryData(): JSX.Element | boolean {
    const data = prepareDefinitionListData(this.getSecondaryData())

    return data.length > 0 && <DefinitionList data={data} />
  }

  render(): JSX.Element | null {
    const {
      userDevice,
      comicbook,
      comicbook: { language, external_links, series_list },
      fromSeries,
      publisherResources,
      emotionRating,
      emotions,
      hasCurrentUserImpression,
      impressionsIndex,
      query,
    } = this.props
    const { uuid, annotation, topics, pages_count, can_be_read } = comicbook

    if (isEmpty(comicbook)) return null

    const isMobileSize = userDevice === 'mobile'

    return (
      <>
        {!isMobileSize && <Spacer size={8} />}
        <ExpandableText
          bookLang={language}
          text={annotation}
          maxHeight={isMobileSize ? 72 : 144}
        />
        <Spacer size={24} />
        {topics && topics.length > 0 && (
          <TopicList>
            {topics.map(topic => (
              <Topic
                {...topic}
                resource="comicbooks"
                key={topic.uuid}
                query={query}
              />
            ))}
          </TopicList>
        )}
        <Spacer size={24} />
        {!can_be_read && <UnavailableInfo kind="comicbook" />}
        {pages_count && <PaperPages paper_pages={pages_count} />}
        <Spacer />
        {comicbook && this.renderSecondaryData()}
        <Spacer size={40} />
        <RelatedOrSeriesEpisodes
          headerRank={2}
          seriesList={series_list}
          fromSeries={fromSeries}
          resourceType="comicbook"
          publisherResources={publisherResources}
          uuid={uuid}
        />
        <Spacer size={64} />
        {external_links && external_links.length > 0 && (
          <List
            headerRank={2}
            kind="index"
            type="slider"
            header="book.book_media_links_title"
          >
            {external_links.map(link => (
              <ExternalLink
                externalLink={link}
                kind={external_links.length > 1 ? 'grid' : 'list'}
              />
            ))}
          </List>
        )}
        <Spacer size={64} />
        <ImpressionsIndex
          headerRank={2}
          query={query}
          resource={comicbook}
          impressionsIndex={impressionsIndex}
          emotionRating={emotionRating}
          emotions={emotions}
          hasCurrentUserImpression={hasCurrentUserImpression}
        />
        <ComicbookShelvesIndexBox headerRank={2} resourceType="comicbook" />
        <Spacer size={24} />
      </>
    )
  }
}

const connectWrapper = connect((state: State) => ({
  userDevice: state.app.size,
  query: state.app.storedQuery,
  comicbook: getComicbookById(state, state.comicbook.uuid),
  impressionsIndex: getImpressionsByIds(
    state,
    state.comicbook.impressionsIndex,
  ),
  emotionRating: state.comicbook.emotionRating,
  publisherResources: state.comicbook.publisherResources,
  emotions: getAllEmotions(state),
  hasCurrentUserImpression: Boolean(state.comicbook.currentUserImpressionUuid),
}))

const wrappers = compose(
  withI18n({ update: true }),
  connectWrapper,
  errorBoundary,
  withMarketing,
)

export default wrappers(ComicbookIndexBox)
